// SPDX-FileCopyrightText: Copyright (c) 2025 Software GmbH, Darmstadt, Germany and/or its subsidiaries and/or its affiliates
// SPDX-FileContributor: Zead Alshukairi (planen-bauen 4.0 GmbH)
// SPDX-FileContributor: Miguel Vega (planen-bauen 4.0 GmbH)
//
// SPDX-License-Identifier: Apache-2.0

import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DidValidators } from 'src/app/construct-x/shared/validators/did-validators';
import { DidEntryMgmtService, DidWebService } from '../../services';
import { DIDEntry, DidGeneratorOutput2 } from '../../models';
import { saveAs } from 'file-saver';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DidService } from '../../services/local/did.service';
import { TranslateModule } from '@ngx-translate/core';
import { SpinnerComponent } from 'src/app/construct-x/shared/components/elems/spinner/spinner.component';
import { PopUpComponent } from 'src/app/construct-x/shared/components/elems/pop-up/pop-up.component';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-did-web-generator',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    SpinnerComponent,
    PopUpComponent
  ],
  templateUrl: './did-web-generator.component.html',
  styleUrls: ['./did-web-generator.component.scss'],
})
export class DidWebGeneratorComponent implements OnInit {

  @Input() label: string;
  @Output() labelChange = new EventEmitter<string>();
  /** When true, component is embedded (e.g. in SD wizard); success popup does not navigate away */
  @Input() embeddedMode = false;
  @Output() didGenerated = new EventEmitter<void>();

  formGroup: FormGroup;

  private ownerId: number | null = null;
  private isInstitution: boolean | null = null;
  certificateType = 'file';
  inProgress = false;
  generating = false;
  errorMessage = '';
  popUpObj: { title: string, message: string, actions: { text: string, action: Function }[] } | null = null;

  generatorOutput: DidGeneratorOutput2 | null = null;
  existedDids: DIDEntry[] = [];

  constructor(
    private fb: FormBuilder, private router: Router, private activeRouter: ActivatedRoute,
    private didWebService: DidWebService, private didEntryService: DidEntryMgmtService, private didService: DidService,
    private cdr: ChangeDetectorRef
  ) { }

  /**
   * Initializes the form and loads existing DIDs.
   */
  ngOnInit(): void {
    this.activeRouter.parent.params.subscribe((params: Params) => {
      this.ownerId = params['organisationId'];
      this.isInstitution = this.router.url.includes('/organisations');
    });
    this.formGroup =
      this.fb.group({
        domain: ['', [Validators.required, DidValidators.isDomain]],
        keySuffix: ['', [Validators.minLength(5), Validators.maxLength(20)]],
        certificate: this.fb.group({
          file: [null, [DidValidators.correctExtension]],
          url: ['', [DidValidators.isUrl, DidValidators.correctUrlExtension]],
          ssl: [false],
          content: ['', [Validators.required]]
        },
          { validators: [DidValidators.fileOrUrlOrSslRequired] }
        )
      });
    this.didService.getDIDs().subscribe(dids => this.existedDids = dids);
  }

  /**
   * Sets the certificate type and loads SSL certificate if needed.
   *
   * @param type the certificate type (file, url, or ssl)
   */
  setCertificateType(type: string) {
    this.certificateType = type;
    if (type == 'ssl' && !this.domain.valid)
      this.showPopUp('did.forms.create.title', 'did.forms.create.domain.required', [{ text: 'did.okay', action: () => this.hidePopUp() }]);
    else if (type == 'ssl' && (!this.content.valid || !this.inProgress)) {
      this.inProgress = true;
      this.generating = true;
      let host = "https://" + this.domain.value;
      this.didWebService.loadSslCertificate(host).subscribe({
        next: (content) => {
          this.ssl.patchValue(true);
          this.content.patchValue(content);
          this.url.reset();
          this.file.reset();
          this.inProgress = false;
          this.generating = false;
          this.showPopUp('did.forms.create.title', 'did.forms.create.certificate.url.loaded', [{ text: 'did.okay', action: () => this.hidePopUp() }]);
        }
      });
    }
  }

  clickFileInput(fileInput: HTMLInputElement) {
    fileInput.click();
  }
  /**
   * Handles certificate file selection and reads its content.
   *
   * @param event the file input change event
   */
  onCertificateSelected(event: Event) {
    this.inProgress = true;
    let input = event.target as HTMLInputElement;
    if (!!input.files && input.files.length == 1) {
      this.file.patchValue(input.files[0]);
      let reader = new FileReader();
      reader.onload = (e: any) => {
        this.content.patchValue(e.target.result as string);
        this.url.reset();
        this.ssl.reset();
        this.inProgress = false;
        this.cdr.detectChanges();
      };
      reader.onerror = () => {
        this.url.enable();
        console.error("Failed to read file");
        this.errorMessage = "file--did.forms.create.certificate.load_failed";
        this.inProgress = false;
        this.cdr.detectChanges();
      };
      reader.readAsText(this.file.value);
    }
    else if (this.file.valid) {
      this.errorMessage = "file--did.forms.create.certificate.load_failed";
      this.inProgress = false;
    }
  }

  getFileName(): string {
    return this.file.value?.name || '';
  }

  /**
   * Loads certificate content from the provided URL.
   */
  resolveUrl(): void {
    if (this.url.valid) {
      let url = this.url.value?.trim();
      this.inProgress = true;
      this.generating = true;
      this.didWebService.loadFileFromUrl("https://" + url).subscribe({
        next: (content) => {
          this.url.patchValue(url);
          this.content.patchValue(content);
          this.file.reset();
          this.ssl.reset();
          this.inProgress = false;
          this.generating = false;
          this.showPopUp('did.forms.create.title', 'did.forms.create.certificate.url.loaded', [{ text: 'did.okay', action: () => this.hidePopUp() }]);
        },
        error: () => {
          console.error("Failed to load file from url");
          this.url.enable();
          this.inProgress = false;
          this.generating = false;
          this.errorMessage = "url--did.forms.create.certificate.load_failed";
          this.cdr.detectChanges();
        }
      });
    }
  }

  /**
   * Generates a new DID document using the form data.
   */
  generateDid(): void {
    this.generating = true;

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.generating = false;
      return;
    }
    this.inProgress = true;

    let didCreatingObj = {
      domain: `https://${this.domain.value}`,
      certificateContent: this.content.value,
      keySuffix: this.keySuffix.value
    }
    this.didWebService.generateDID(didCreatingObj, this.ownerId).subscribe(res => {
      if (res.status == "success") {
        this.generatorOutput = res.value;
        this.storeDID(res.value.did);
        this.inProgress = false;
        this.generating = false;
      }
      else {
        this.inProgress = false;
        this.generating = false;
        this.showPopUp('did.forms.create.title', res.message, [{ text: 'did.okay', action: () => this.hidePopUp() }]);
      }
    });
  }

  /**
   * Stores the generated DID in the backend.
   *
   * @param did the DID string to store
   */
  private storeDID(did: string): void {
    this.inProgress = true;
    this.didEntryService.storeUserDID(this.ownerId, this.isInstitution, did, this.label, this.keySuffix.value).subscribe({
      next: () => {
        this.inProgress = false;
        this.resetForm();
        let actions = [
          { text: 'did.download', action: this.downloadDidDocument.bind(this) },
          { text: 'did.okay', action: () => this.hidePopUp("overview") }
        ];
        this.showPopUp('did.forms.create.title', 'did.forms.create.success_msg', actions);
        this.didGenerated.emit();
      },
      error: (error) => {
        console.error("Failed to store DID", error);
        this.inProgress = false;
        this.generating = false;
        this.showPopUp('did.forms.create.title', 'did.forms.create.error_msg', [{ text: 'did.okay', action: () => this.hidePopUp() }]);
      }
    });
  }

  /**
   * Downloads the DID document as a JSON file.
   */
  downloadDidDocument(): void {
    if (this.generatorOutput?.didDocument) {
      let blob = new Blob(
        [JSON.stringify(this.generatorOutput.didDocument, null, 2)],
        { type: 'application/json' }
      );
      let filename = this.label ? `${this.label}.json` : 'did.json';
      saveAs(blob, filename);
      this.hidePopUp("overview");
    }
  }

  /**
   * Checks if the key suffix already exists in existing DIDs.
   *
   * @returns true if the key suffix exists, otherwise false
   */
  public checkKeySuffixIfExists(): boolean {
    return this.existedDids.some(did => did.keySuffix == "#" + this.keySuffix.value);
  }

  /**
   * Gets the domain validation error message.
   *
   * @returns the error message key or empty string
   */
  getDomainValidationError(): string {
    if (this.domain.touched && this.domain.hasError('required'))
      return 'did.forms.create.domain.required';
    if (this.domain.touched && this.domain.hasError('domainInvalid'))
      return 'did.forms.create.domain.invalid';
    if (this.errorMessage.length > 0 && this.errorMessage.startsWith("domain--"))
      return this.errorMessage.split('--')[1];
    return '';
  }

  /**
   * Gets the key suffix validation error message.
   *
   * @returns the error message key or empty string
   */
  getKeySuffixValidationError(): string {
    if (this.keySuffix.touched && (this.keySuffix.hasError('minlength') || this.keySuffix.hasError('maxlength')))
      return 'did.forms.create.key_suffix.invalid';
    if (this.keySuffix.touched && this.checkKeySuffixIfExists())
      return 'did.forms.create.key_suffix.exists';
    return '';
  }

  /**
   * Gets the certificate file validation error message.
   *
   * @returns the error message key or empty string
   */
  getFileValidationError(): string {
    if ((this.file.touched || this.url.touched) && this.certificate.hasError('fileOrUrlOrSslRequired'))
      return 'did.forms.create.certificate.file.required';
    if (this.certificate.invalid && this.file.hasError('wrongExtension'))
      return 'did.forms.create.certificate.file.invalid';
    if (this.errorMessage.length > 0 && this.errorMessage.startsWith("file--"))
      return this.errorMessage.split('--')[1];
    return '';
  }

  /**
   * Gets the certificate URL validation error message.
   *
   * @returns the error message key or empty string
   */
  getUrlValidationError(): string {
    if ((this.file.touched || this.url.touched) && this.certificate.hasError('fileOrUrlOrSslRequired'))
      return 'did.forms.create.certificate.url.required';
    if (this.url.touched && this.url.hasError('urlInvalid'))
      return 'did.forms.create.certificate.url.invalid';
    if (this.url.touched && this.url.hasError('wrongUrlExtension'))
      return 'did.forms.create.certificate.url.invalid_extension';
    if (this.errorMessage.length > 0 && this.errorMessage.startsWith("url--"))
      return this.errorMessage.split('--')[1];
    return '';
  }

  /**
   * Shows a popup with the specified title, message, and actions.
   *
   * @param title the popup title
   * @param message the popup message
   * @param actions the available actions
   */
  private showPopUp(title: string, message: string, actions: { text: string, action: Function }[]) {
    this.popUpObj = {
      title: title,
      message: message,
      actions: actions
    };
    this.cdr.detectChanges();
  }

  /**
   * Hides the popup and optionally navigates to a activeRouter.
   *
   * @param path the activeRouter path to navigate to (optional)
   */
  private hidePopUp(path: string = "") {
    this.popUpObj = null;
    if (!this.embeddedMode && path.length > 0) {
      this.router.navigate(['..', path], { relativeTo: this.activeRouter });
    }
  }

  private resetForm() {
    this.formGroup.reset();
    this.url.enable();
    this.ssl.patchValue(false);
    this.labelChange.emit("");
    this.errorMessage = "";
    this.popUpObj = null;
    this.inProgress = false;
  }

  get domain(): AbstractControl { return this.formGroup.get('domain'); }
  get keySuffix(): AbstractControl { return this.formGroup.get('keySuffix'); }
  get certificate(): AbstractControl { return this.formGroup.get('certificate'); }
  get file(): AbstractControl { return this.certificate.get('file'); }
  get url(): AbstractControl { return this.certificate.get('url'); }
  get ssl(): AbstractControl { return this.certificate.get('ssl'); }
  get content(): AbstractControl { return this.certificate.get('content'); }

}
